import { useQuery, useMutation } from "react-query";
import { Award, Klass, Student } from "../types";
import firebase from "firebase";
import { nanoid } from "nanoid";

const useStudents = () => {
  const { isLoading, error, data, refetch } = useQuery<Student[]>(
    "students",
    () =>
      firebase
        .firestore()
        .collection("students")
        .get()
        .then((snapshot) => {
          const students: Student[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data() as Student;
            students.push({ ...data, id: doc.id });
          });
          return students;
        })
  );

  const { mutate: addStudentToFirestore } = useMutation(
    "students",
    async (variables: Student) => {
      await firebase
        .firestore()
        .collection("students")
        .doc(nanoid())
        .set(variables);
      return variables;
    }
  );

  const giveAward = async (nextAw: Partial<Award>, studentId: string) => {
    const nextAward: Award = {
      id: nanoid(),
      seen: false,
      title: "Good work!",
      description: `You have been given an award!`,
      teacher: "unknown",
      stars: 100,
      date: Date.now(),
      ...nextAw,
    };
    // Give student the award
    const studentDetails = await firebase
      .firestore()
      .collection("students")
      .doc(studentId)
      .get();
    firebase
      .firestore()
      .collection("students")
      .doc(studentId)
      .update({
        awards: [...(studentDetails.data()?.awards || []), nextAward],
      });
  };

  const addStudentsToClass = async (studentIds: string[], klass: Klass) => {
    const nextClass: Klass = {
      ...klass,
      students: [
        ...(klass?.students || []),
        ...studentIds.map((studentId) => ({ id: studentId })),
      ],
    };
    await firebase.firestore().collection("classes").doc(klass.id).update({
      students: nextClass.students,
    });
    return nextClass;
  };

  const removeStudentToClass = async (studentId: string, klass: Klass) => {
    const nextClass: Klass = {
      ...klass,
      students: (klass?.students || []).filter((x) => x.id !== studentId),
    };
    await firebase.firestore().collection("classes").doc(klass.id).update({
      students: nextClass.students,
    });

    return nextClass;
  };

  return {
    isLoading,
    error,
    data,
    addStudentToFirestore,
    addStudentsToClass,
    removeStudentToClass,
    giveAward,
    refetch,
  };
};

export default useStudents;
